import "./portal.css";

import Auth, { useUser } from "@dashboard/auth";
import LoginLoading from "@dashboard/auth/components/LoginLoading/LoginLoading";
import { useAuthRedirection } from "@dashboard/auth/hooks/useAuthRedirection";
import { hasPermission } from "@dashboard/auth/misc";
import { Route } from "@dashboard/components/Router";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { PermissionEnum, usePortalHomeQuery } from "@dashboard/graphql";
import { operatorMessages } from "@dashboard/operator/messages";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import {
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Clock,
  FileText,
  Hourglass,
  LayoutDashboard,
  ListChecks,
  Plane,
  Settings,
  User,
  Users as UsersIcon,
} from "lucide-react";
import { useIntl } from "react-intl";
import { Redirect, Switch } from "react-router-dom";

import { getKioskToken } from "./device";
import { LanguageSwitch } from "./LanguageSwitch";
import { portalMessages as m } from "./messages";
import { NotificationsBell } from "./NotificationsBell";
import { PolicyGate } from "./PolicyGate";
import { PortalLayout, PortalModule } from "./PortalLayout";
import { ProfileCompletion } from "./ProfileCompletion";
import { portalPaths } from "./urls";
import { today } from "./utils";
import { Absences } from "./views/Absences";
import { Employee } from "./views/Employee";
import { ManageTasks } from "./views/ManageTasks";
import { Notifications } from "./views/Notifications";
import { Overtime } from "./views/Overtime";
import { PinScreen } from "./views/PinScreen";
import { Profile } from "./views/Profile";
import { Records } from "./views/Records";
import { Roster } from "./views/Roster";
import { RotaPlanner } from "./views/RotaPlanner";
import { Setup } from "./views/Setup";
import { Tasks } from "./views/Tasks";
import { Team } from "./views/Team";
import { Today } from "./views/Today";
import { Users } from "./views/Users";

const NoProfile = ({ canManage }: { canManage: boolean }) => {
  const intl = useIntl();
  const { logout } = useUser();

  return (
    <Box display="grid" placeItems="center" __minHeight="60vh" padding={6}>
      <Box display="flex" flexDirection="column" gap={4} __maxWidth={420}>
        <Text size={8} fontWeight="bold">
          {intl.formatMessage(m.noProfileTitle)}
        </Text>
        <Text color="default2">{intl.formatMessage(m.noProfileBody)}</Text>
        {!canManage && (
          <Box>
            <Button variant="primary" onClick={() => logout?.()}>
              {intl.formatMessage(operatorMessages.logout)}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const PortalApp = () => {
  const { user } = useUser();
  const canManage = !!user && hasPermission(PermissionEnum.MANAGE_TIMEKEEPING, user);
  const { data, loading, refetch } = usePortalHomeQuery({
    variables: { today: today(), kioskToken: getKioskToken() },
    fetchPolicy: "cache-and-network",
    pollInterval: 60000,
  });

  if (loading && !data) {
    return <LoginLoading />;
  }

  const isEmployee = !!data?.myEmployeeProfile;
  const pending = data?.pendingPolicies ?? [];
  const icon = 18;
  const modules: PortalModule[] = [
    ...(isEmployee
      ? ([
          {
            id: "today",
            path: portalPaths.today,
            title: m.todayModule,
            icon: <Clock size={icon} />,
          },
          {
            id: "records",
            path: portalPaths.records,
            title: m.recordsModule,
            icon: <FileText size={icon} />,
          },
          {
            id: "roster",
            path: portalPaths.roster,
            title: m.rosterModule,
            icon: <CalendarDays size={icon} />,
          },
          ...(data?.myWorkSettings?.tasksEnabled || data?.myWorkSettings?.dailyReportRequired
            ? [
                {
                  id: "tasks",
                  path: portalPaths.tasks,
                  title: m.tasksModule,
                  icon: <ListChecks size={icon} />,
                },
              ]
            : []),
          {
            id: "absences",
            path: portalPaths.absences,
            title: m.absencesModule,
            icon: <Plane size={icon} />,
          },
          {
            id: "overtime",
            path: portalPaths.overtime,
            title: m.overtimeModule,
            icon: <Hourglass size={icon} />,
          },
          {
            id: "profile",
            path: portalPaths.profile,
            title: m.profileModule,
            icon: <User size={icon} />,
          },
        ].map(module => ({ ...module, section: "mine" })) as PortalModule[])
      : []),
    ...(canManage
      ? ([
          {
            id: "team",
            path: portalPaths.team,
            title: m.teamModule,
            icon: <LayoutDashboard size={icon} />,
          },
          {
            id: "rota",
            path: portalPaths.rota,
            title: m.shiftsPlannerModule,
            icon: <CalendarRange size={icon} />,
          },
          {
            id: "manage-tasks",
            path: portalPaths.manageTasks,
            title: m.manageTasksModule,
            icon: <ClipboardList size={icon} />,
          },
          {
            id: "users",
            path: portalPaths.users,
            title: m.usersModule,
            icon: <UsersIcon size={icon} />,
          },
          {
            id: "setup",
            path: portalPaths.setup,
            title: m.setupModule,
            icon: <Settings size={icon} />,
          },
        ].map(module => ({ ...module, section: "manage" })) as PortalModule[])
      : []),
  ];
  const missingFields = data?.myEmployeeProfile?.missingFields ?? [];

  return (
    <PortalLayout
      modules={modules}
      lastLogin={data?.myLastLogin}
      actions={
        <>
          <NotificationsBell unread={data?.myUnreadNotifications ?? 0} />
          <LanguageSwitch />
        </>
      }
    >
      {isEmployee && pending.length > 0 ? (
        <PolicyGate documents={pending} onDone={() => refetch()} />
      ) : isEmployee && missingFields.length > 0 ? (
        <ProfileCompletion onDone={() => refetch()} />
      ) : (
        <Switch>
          {isEmployee && <Route exact path={portalPaths.today} component={Today} />}
          {isEmployee && <Route path={portalPaths.records} component={Records} />}
          {isEmployee && <Route path={portalPaths.roster} component={Roster} />}
          {isEmployee && <Route path={portalPaths.absences} component={Absences} />}
          {isEmployee && <Route path={portalPaths.tasks} component={Tasks} />}
          <Route path={portalPaths.notifications} component={Notifications} />
          {canManage && <Route path={portalPaths.manageTasks} component={ManageTasks} />}
          {canManage && <Route path={portalPaths.pinScreen} component={PinScreen} />}
          {isEmployee && <Route path={portalPaths.overtime} component={Overtime} />}
          {isEmployee && <Route path={portalPaths.profile} component={Profile} />}
          {canManage && <Route path={portalPaths.employee} component={Employee} />}
          {canManage && <Route path={portalPaths.team} component={Team} />}
          {canManage && <Route path={portalPaths.setup} component={Setup} />}
          {canManage && <Route path={portalPaths.rota} component={RotaPlanner} />}
          {canManage && <Route path={portalPaths.users} component={Users} />}
          {modules.length > 0 ? (
            <Redirect to={modules[0].path} />
          ) : (
            <Route component={() => <NoProfile canManage={!!canManage} />} />
          )}
        </Switch>
      )}
    </PortalLayout>
  );
};

/**
 * Routes for the employee portal (see portalHost.ts): the colaborador's own
 * time book, roster and overtime, plus management screens for
 * MANAGE_TIMEKEEPING. Nothing else in the dashboard is reachable here.
 */
export const PortalRoutes = () => {
  const intl = useIntl();
  const { authenticated, authenticating } = useAuthRedirection();

  return (
    <>
      <WindowTitle title={intl.formatMessage(m.platformName)} />
      {authenticated ? <PortalApp /> : authenticating ? <LoginLoading /> : <Auth />}
    </>
  );
};
